import { useCallback, useEffect, useState } from 'react';
import {
  fetchGramBalance,
  fetchLpPosition,
  fetchRefBalance,
  fetchVaultState,
  type LpPosition,
  type VaultState,
} from '../ton/vault';

export interface VaultData {
  vault: VaultState | null;
  lp: LpPosition | null;
  refBalance: bigint;
  gramBalance: bigint;
  /** GRAM value of the holder's LP at the live bank price, nano. */
  lpValue: bigint;
  refresh: () => void;
}

/**
 * Live mini-games bank data for the Assets page: the vault state (bank, LP
 * supply), the connected wallet's LP position/value, its claimable referral
 * balance and native GRAM balance. Refreshes on demand and every minute.
 */
export function useVaultData(address: string | null): VaultData {
  const [vault, setVault] = useState<VaultState | null>(null);
  const [lp, setLp] = useState<LpPosition | null>(null);
  const [refBalance, setRefBalance] = useState(0n);
  const [gramBalance, setGramBalance] = useState(0n);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    void fetchVaultState()
      .then((s) => {
        if (!cancelled) setVault(s);
      })
      .catch(() => undefined);
    if (address) {
      void fetchLpPosition(address)
        .then((p) => {
          if (!cancelled) setLp(p);
        })
        .catch(() => undefined);
      void fetchRefBalance(address)
        .then((b) => {
          if (!cancelled) setRefBalance(b);
        })
        .catch(() => undefined);
      void fetchGramBalance(address)
        .then((b) => {
          if (!cancelled) setGramBalance(b);
        })
        .catch(() => undefined);
    } else {
      setLp(null);
      setRefBalance(0n);
      setGramBalance(0n);
    }
    const timer = setInterval(refresh, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [address, tick, refresh]);

  const lpValue =
    vault && lp && vault.totalShares > 0n
      ? (lp.balance * vault.bankroll) / vault.totalShares
      : 0n;

  return { vault, lp, refBalance, gramBalance, lpValue, refresh };
}
